import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Tooltip } from '@systemeio/ui'
import { LectureId } from 'modules/courses/courses/curriculum/types/lecture-interface'
import { nonDraggableProps } from 'modules/courses/courses/curriculum/utils/non-draggable-props'
import React from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twMerge } from 'tailwind-merge'
import { CheckCircleIcon, LectureStatusIcon } from '@systemeio/ui'

interface ModuleLectureProps {
  lecture: {
    id: LectureId
    name: string
    pageEditUrl?: string
    active: boolean
    delayBeforePreviousLecture?: number
    hasAssignmentSettings?: boolean
    assignmentActivatedAt?: string | null
  }
  disabled: boolean
  isLastChild: boolean
  isFetching?: boolean
  lectureActions: React.ReactNode
  onNameClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLDivElement | HTMLButtonElement>
}

export function ModuleLecture({
  lecture,
  disabled,
  isLastChild,
  isFetching,
  lectureActions,
  onNameClick,
}: ModuleLectureProps) {
  const { t } = useLocoTranslation()

  const { setNodeRef, listeners, transform, transition } = useSortable({
    id: lecture.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      className={twMerge(
        'flex flex-col ',
        isLastChild && 'rounded-b-lg',
        'even:bg-brand-primary-subtle odd:bg-background-secondary items-start gap-4 lg:flex-row lg:items-center lg:gap-0',
        'hover:border-brand-primary main-transition-colors cursor-move border border-transparent',
        disabled && 'cursor-not-allowed',
        isFetching && 'animate-pulse',
      )}
      style={style}
      ref={disabled ? undefined : setNodeRef}
      {...listeners}
    >
      <div className="hidden w-full flex-[55_1_0%] truncate px-4 py-4 lg:block lg:px-6">
        <div className="flex items-center gap-3">
          {lecture.pageEditUrl ? (
            <a
              className="primary-link cursor-pointer truncate text-sm font-normal"
              {...nonDraggableProps}
              href={lecture.pageEditUrl}
              onClick={onNameClick}
              data-test={'module-lecture-link'}
            >
              {lecture.name}
            </a>
          ) : (
            <button
              className="primary-link cursor-pointer truncate text-sm font-normal"
              {...nonDraggableProps}
              onClick={onNameClick}
              data-test={'module-lecture-name'}
            >
              {lecture.name}
            </button>
          )}
        </div>
      </div>
      <div className="block w-full truncate px-4 py-2  lg:hidden lg:px-6">
        <div className="flex items-center gap-3 ">
          <div
            className="text-brand-primary flex-[87_1_0%] cursor-default truncate text-sm font-normal"
            {...nonDraggableProps}
            onClick={onNameClick}
            data-test={'module-lecture-name'}
          >
            {lecture.name}
          </div>
          <div className="flex flex-[15_1_0%] items-center  justify-between gap-3">
            <span className="text-text-primary whitespace-nowrap font-bold">
              <div className="flex cursor-default items-center lg:ml-4" {...nonDraggableProps}>
                <Tooltip
                  label={lecture.active ? t('global.active') : t('global.inactive')}
                  mode="hover"
                  key="remove-tooltip"
                >
                  <CheckCircleIcon
                    className={`w-[20px]h-[20px] ${lecture.active && 'fill-green'}`}
                  />
                </Tooltip>
              </div>
            </span>
            {lecture.hasAssignmentSettings && (
              <Tooltip
                label={
                  lecture.assignmentActivatedAt
                    ? t('dashboard.course.lecture_assignment_active')
                    : t('dashboard.course.lecture_assignment_inactive')
                }
                mode="hover"
              >
                <LectureStatusIcon
                  className={twMerge(
                    `h-[20px] w-[20px]`,
                    lecture.assignmentActivatedAt ? 'fill-brand-primary' : 'fill-fill-primary',
                  )}
                />
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      {lecture.delayBeforePreviousLecture !== undefined && (
        <div className="text-text-primary w-full flex-[15_1_0%] truncate px-4 py-2 text-sm font-normal lg:block lg:px-6">
          <div className="flex flex-col gap-1">
            <span data-test={'module-lecture-delay'}>
              {lecture.delayBeforePreviousLecture === 0
                ? t('dashboard.course.lecture_grant_access_immediately')
                : t('dashboard.course.lecture_days_delay_title', {
                    daysCount: lecture.delayBeforePreviousLecture,
                  })}
            </span>
            <span className="opacity-60">
              ({t('dashboard.course.lecture_days_delay_subtitle')})
            </span>
          </div>
        </div>
      )}
      <div className="hidden w-full flex-[10_1_0%] truncate px-4 py-2 lg:block lg:px-6 lg:py-4">
        <div className="flex items-center gap-3">
          <span className="text-text-primary whitespace-nowrap font-bold">
            <div
              className="flex cursor-default items-center lg:ml-4"
              {...nonDraggableProps}
              data-test={'module-lecture-status'}
            >
              <Tooltip
                label={lecture.active ? t('global.active') : t('global.inactive')}
                mode="hover"
                key="remove-tooltip"
              >
                <CheckCircleIcon
                  className={`h-[16px] w-[16px] sm:h-[20px] sm:w-[20px] ${
                    lecture.active && 'fill-green'
                  }`}
                />
              </Tooltip>
            </div>
          </span>
        </div>
      </div>
      <div className={'flex flex-[10_1_0%] items-center justify-end gap-6 px-4 py-4 lg:px-6'}>
        {lecture.hasAssignmentSettings && (
          <Tooltip
            wrapperClassName={'lg:block hidden'}
            label={
              lecture.assignmentActivatedAt
                ? t('dashboard.course.lecture_assignment_active')
                : t('dashboard.course.lecture_assignment_inactive')
            }
            mode="hover"
          >
            <LectureStatusIcon
              className={twMerge(
                `h-[20px] w-[20px]`,
                lecture.assignmentActivatedAt ? 'fill-brand-primary' : 'fill-fill-primary',
              )}
            />
          </Tooltip>
        )}
        {lectureActions}
      </div>
    </div>
  )
}
